class TexanPartsFinder extends HTMLElement {
  connectedCallback() {
    this.searchUrl = this.dataset.searchUrl || '/search';
    this.optionsUrl = this.dataset.optionsUrl || '';
    this.tree = {};
    this.powerTree = {};
    this.marineTree = {};
    this.lists = this.readLists();

    this.tabs = Array.from(this.querySelectorAll('[data-finder-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-finder-panel]'));
    this.heading = this.querySelector('[data-finder-heading]');
    this.form = this.querySelector('[data-finder-form]');
    this.submitBtn = this.querySelector('[data-finder-submit]');

    this.autoYear = this.querySelector('[data-auto-year]');
    this.autoMake = this.querySelector('[data-auto-make]');
    this.autoModel = this.querySelector('[data-auto-model]');
    this.autoEngine = this.querySelector('[data-auto-engine]');

    this.marineYear = this.querySelector('[data-marine-year]');
    this.marineMake = this.querySelector('[data-marine-make]');
    this.marineModel = this.querySelector('[data-marine-model]');

    this.powerBrand = this.querySelector('[data-power-brand]');
    this.powerType = this.querySelector('[data-power-type]');
    this.powerModel = this.querySelector('[data-power-model]');

    this.browseLinks = Array.from(this.querySelectorAll('[data-finder-browse]'));

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.setCategory(tab.dataset.finderTab));
    });

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.submit();
    });

    this.autoYear?.addEventListener('change', () => this.onAutoYearChange());
    this.autoMake?.addEventListener('change', () => this.onAutoMakeChange());
    this.autoModel?.addEventListener('change', () => this.onAutoModelChange());
    this.autoEngine?.addEventListener('change', () => this.updateSubmitState());

    this.marineYear?.addEventListener('change', () => this.updateSubmitState());
    this.marineMake?.addEventListener('change', () => this.onMarineMakeChange());
    this.marineModel?.addEventListener('change', () => this.updateSubmitState());

    this.powerBrand?.addEventListener('change', () => this.onPowerBrandChange());
    this.powerType?.addEventListener('change', () => this.onPowerTypeChange());
    this.powerModel?.addEventListener('change', () => this.updateSubmitState());

    this.populateStaticFields();
    this.setCategory(this.dataset.defaultCategory || 'automotive');
    this.loadAutomotiveOptions();
  }

  readLists() {
    const node = this.querySelector('[data-finder-lists]');
    if (!node) return { marine: {}, power: {} };
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return { marine: {}, power: {} };
    }
  }

  readInlineOptions() {
    const node = this.querySelector('[data-finder-options]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '{}');
    } catch (_) {
      return null;
    }
  }

  applyAutomotiveTree(tree) {
    this.tree = tree || {};
    const years = Object.keys(this.tree).sort((a, b) => Number(b) - Number(a));
    this.fillSelect(this.autoYear, years, 'Year');
    this.autoYear.disabled = years.length === 0;
  }

  applyEquipmentTrees(payload) {
    this.powerTree = (payload && payload.power && payload.power.tree) || {};
    this.marineTree = (payload && payload.marine && payload.marine.tree) || {};
    this.refreshPowerModels();
    this.refreshMarineModels();
  }

  splitLines(value) {
    return String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  currentYear() {
    return new Date().getFullYear();
  }

  yearRange(start, end) {
    const years = [];
    for (let year = end; year >= start; year -= 1) years.push(String(year));
    return years;
  }

  fillSelect(select, values, placeholder) {
    if (!select || select.tagName !== 'SELECT') return;
    const current = select.value;
    select.innerHTML = '';
    const option = document.createElement('option');
    option.value = '';
    option.textContent = placeholder;
    select.appendChild(option);
    values.forEach((value) => {
      const item = document.createElement('option');
      item.value = value;
      item.textContent = value;
      select.appendChild(item);
    });
    if (current && values.includes(current)) select.value = current;
  }

  lookupNode(tree, key) {
    if (!tree || !key) return null;
    if (Object.prototype.hasOwnProperty.call(tree, key)) return tree[key];
    const found = Object.keys(tree).find((item) => item.toLowerCase() === key.toLowerCase());
    return found ? tree[found] : null;
  }

  populateStaticFields() {
    const marineYears = this.splitLines(this.lists.marine?.years);
    this.fillSelect(
      this.marineYear,
      marineYears.length ? marineYears : this.yearRange(1990, this.currentYear()),
      'Year'
    );
    this.fillSelect(this.marineMake, this.splitLines(this.lists.marine?.makes), 'Make');
    this.fillSelect(this.powerBrand, this.splitLines(this.lists.power?.brands), 'Brand');
    this.fillSelect(this.powerType, this.splitLines(this.lists.power?.types), 'Equipment type');
    this.refreshMarineModels();
    this.refreshPowerModels();
  }

  fallbackModels(kind) {
    return this.splitLines(this.lists[kind]?.models);
  }

  refreshMarineModels() {
    const make = this.fieldValue(this.marineMake);
    const fromTree = make ? this.lookupNode(this.marineTree, make) : null;
    const models = Array.isArray(fromTree) && fromTree.length ? fromTree : this.fallbackModels('marine');
    this.fillSelect(this.marineModel, models, 'Any model');
    if (this.marineModel) this.marineModel.disabled = !make;
  }

  refreshPowerModels() {
    const brand = this.fieldValue(this.powerBrand);
    const type = this.fieldValue(this.powerType);
    const brandNode = brand ? this.lookupNode(this.powerTree, brand) : null;
    const typeNode = brandNode && type ? this.lookupNode(brandNode, type) : null;
    const models = Array.isArray(typeNode) && typeNode.length ? typeNode : this.fallbackModels('power');
    this.fillSelect(this.powerModel, models, 'Any model');
    if (this.powerModel) this.powerModel.disabled = !(brand && type);
  }

  onMarineMakeChange() {
    this.refreshMarineModels();
    this.updateSubmitState();
  }

  onPowerBrandChange() {
    this.refreshPowerModels();
    this.updateSubmitState();
  }

  onPowerTypeChange() {
    this.refreshPowerModels();
    this.updateSubmitState();
  }

  async loadAutomotiveOptions() {
    if (!this.autoYear) return;
    const inline = this.readInlineOptions();
    let tree = inline && inline.tree ? inline.tree : null;
    let syncedAt = (inline && inline.synced_at) || '';
    let payload = inline;

    if (this.optionsUrl) {
      try {
        const response = await fetch(this.optionsUrl, { credentials: 'same-origin' });
        if (response.ok) {
          const assetPayload = await response.json();
          const assetTree = assetPayload.tree || {};
          const assetSynced = assetPayload.synced_at || '';
          if (!tree || !syncedAt || assetSynced >= syncedAt) {
            tree = assetTree;
            payload = assetPayload;
          }
        }
      } catch (_) {
        /* fall back to inline shop metafield JSON when the theme asset is missing */
      }
    }

    if (tree) this.applyAutomotiveTree(tree);
    this.applyEquipmentTrees(payload);
    this.updateSubmitState();
  }

  setCategory(category) {
    this.category = category;
    this.tabs.forEach((tab) => {
      const active = tab.dataset.finderTab === category;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      tab.tabIndex = active ? 0 : -1;
    });
    this.panels.forEach((panel) => {
      const active = panel.dataset.finderPanel === category;
      panel.hidden = !active;
    });
    if (this.heading) {
      const labels = {
        automotive: 'FIND PARTS FOR YOUR VEHICLE',
        marine: 'FIND PARTS FOR YOUR BOAT',
        power: 'FIND PARTS FOR YOUR EQUIPMENT',
      };
      this.heading.textContent = labels[category] || labels.automotive;
    }
    this.browseLinks.forEach((link) => {
      link.hidden = link.dataset.finderBrowse !== category;
    });
    this.updateSubmitState();
  }

  modelsFor(year, make) {
    const node = year ? this.tree[year]?.[make] : null;
    if (!node) return [];
    if (Array.isArray(node)) return node;
    return Object.keys(node);
  }

  enginesFor(year, make, model) {
    const node = year && make ? this.tree[year]?.[make] : null;
    if (!node || Array.isArray(node) || !model) return [];
    const engines = node[model];
    return Array.isArray(engines) ? engines : [];
  }

  resetEngine() {
    this.fillSelect(this.autoEngine, [], 'Engine');
    if (this.autoEngine) this.autoEngine.disabled = true;
  }

  onAutoYearChange() {
    const year = this.autoYear.value;
    const makes = year ? Object.keys(this.tree[year] || {}) : [];
    this.fillSelect(this.autoMake, makes, 'Make');
    this.autoMake.disabled = !year;
    this.fillSelect(this.autoModel, [], 'Model');
    this.autoModel.disabled = true;
    this.resetEngine();
    this.updateSubmitState();
  }

  onAutoMakeChange() {
    const year = this.autoYear.value;
    const make = this.autoMake.value;
    const models = this.modelsFor(year, make);
    this.fillSelect(this.autoModel, models, 'Model');
    this.autoModel.disabled = !make;
    this.resetEngine();
    this.updateSubmitState();
  }

  onAutoModelChange() {
    const year = this.autoYear.value;
    const make = this.autoMake.value;
    const model = this.autoModel.value;
    const engines = this.enginesFor(year, make, model);
    this.fillSelect(this.autoEngine, engines, 'Engine');
    if (this.autoEngine) {
      this.autoEngine.disabled = !model || engines.length === 0;
      if (engines.length === 1) this.autoEngine.value = engines[0];
    }
    this.updateSubmitState();
  }

  fieldValue(field) {
    return (field?.value || '').trim();
  }

  isComplete() {
    if (this.category === 'marine') {
      return Boolean(this.fieldValue(this.marineYear) && this.fieldValue(this.marineMake));
    }
    if (this.category === 'power') {
      return Boolean(this.fieldValue(this.powerBrand) && this.fieldValue(this.powerType));
    }
    return Boolean(
      this.fieldValue(this.autoYear) &&
        this.fieldValue(this.autoMake) &&
        this.fieldValue(this.autoModel) &&
        (this.enginesFor(
          this.fieldValue(this.autoYear),
          this.fieldValue(this.autoMake),
          this.fieldValue(this.autoModel)
        ).length === 0 ||
          this.fieldValue(this.autoEngine))
    );
  }

  updateSubmitState() {
    if (this.submitBtn) this.submitBtn.disabled = !this.isComplete();
  }

  queryTerms() {
    if (this.category === 'marine') {
      return [this.fieldValue(this.marineYear), this.fieldValue(this.marineMake), this.fieldValue(this.marineModel)];
    }
    if (this.category === 'power') {
      return [this.fieldValue(this.powerBrand), this.fieldValue(this.powerType), this.fieldValue(this.powerModel)];
    }
    return [
      this.fieldValue(this.autoYear),
      this.fieldValue(this.autoMake),
      this.fieldValue(this.autoModel),
      this.fieldValue(this.autoEngine),
    ];
  }

  quoteSearchTerm(term) {
    const cleaned = String(term || '').trim().replace(/"/g, '');
    if (!cleaned) return '';
    if (/^[A-Za-z]+$/.test(cleaned)) return cleaned;
    return `"${cleaned}"`;
  }

  searchQuery() {
    return this.queryTerms()
      .map((term) => this.quoteSearchTerm(term))
      .filter(Boolean)
      .join(' AND ');
  }

  submit() {
    if (!this.isComplete()) return;
    const url = new URL(this.searchUrl, window.location.origin);
    url.searchParams.set('q', this.searchQuery());
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    window.location.assign(url.toString());
  }
}

if (!customElements.get('texan-parts-finder')) {
  customElements.define('texan-parts-finder', TexanPartsFinder);
}
